/**
 * Central feature registry — the single source of truth for what the app can do.
 *
 * The Home page renders these: `live` features become clickable cards, `planned`
 * ones show as "coming soon". When you ship a new section, flip its status to
 * `live` (and add its route) here — the home page updates itself. One line.
 */
export type FeatureStatus = "live" | "planned";

export interface Feature {
  key: string;
  title: string;
  emoji: string;
  description: string;
  /**
   * Section slug this card links to, relative to a space (e.g. "pets"). The
   * space home builds the full path as `/{slug}/{section}`. Undefined for
   * planned features.
   */
  section?: string;
  status: FeatureStatus;
  /** Accent color for the card (kept here so the palette stays in one place). */
  accent: string;
}

export const FEATURES: Feature[] = [
  {
    key: "pets",
    title: "Pet Care",
    emoji: "🐾",
    description: "A shared care calendar and a photo wall of our pets' best moments.",
    section: "pets",
    status: "live",
    accent: "#38bdf8",
  },
  {
    key: "reading",
    title: "Reading Notes",
    emoji: "📚",
    description: "Books we've read — covers, ratings, feelings, and good sentences (bilingual).",
    section: "reading",
    status: "live",
    accent: "#34d399",
  },
  {
    key: "travel",
    title: "Travel Map",
    emoji: "✈️",
    description: "A world map of every city we've visited — pins, ratings, and bilingual travel guides.",
    section: "travel",
    status: "live",
    accent: "#f472b6",
  },
  {
    key: "cooking",
    title: "Cooking Menu",
    emoji: "🍳",
    description: "Every dish we love to make — bilingual menu, ratings, and a request-to-cook cart.",
    section: "cooking",
    status: "live",
    accent: "#fbbf24",
  },
  {
    key: "diary",
    title: "Love Diary",
    emoji: "💞",
    description: "Our days together — important dates, travels, the moments we treasure, and remarkable memories.",
    section: "diary",
    status: "live",
    accent: "#a78bfa",
  },
  {
    key: "guides",
    title: "Travel Guides",
    emoji: "🧭",
    description: "Hand-made trip notebooks — day-by-day plans, sketch maps and tips, grouped by country.",
    section: "guides",
    status: "live",
    accent: "#2dd4bf",
  },
];

export const liveFeatures = (): Feature[] => FEATURES.filter((f) => f.status === "live");
export const plannedFeatures = (): Feature[] => FEATURES.filter((f) => f.status === "planned");
